import React, {useCallback, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useDropzone} from "react-dropzone";
import makeStyles from "@material-ui/styles/makeStyles";
import {Typography} from "@material-ui/core";
import clsx from "clsx";
import {uid} from "react-uid";
import JsonScreenHoc from "../../../../../../HocHelpers/JsonScreenHoc";
import { multiLanguageValue } from "../../../../Utils/lib";
import {getParamsFromString} from "../../../../Utils";
import request from "../../../../../../Services/API/Requests";
import {setFieldValue} from "../../../../jsonScreen.slice";
import {setArrayPath} from "../../../../../../Utils/Form/FormDynamic/Utils/setArrayPath";
import findByString from "../../../../../../Utils/Form/FormDynamic/Utils/getValueByString";
import IconCustom from "../../../../../../Utils/IconCustom/IconCustom";
import DroppedFilesList from "./DroppedFiles";
import {checkHidden} from "../../../../../../Utils/Form/FormDynamic/Utils/checkHidden";

const useStyles = makeStyles((theme) => ({
    container: {
        border: '2px dashed #eeeeee',
        borderRadius: '2px',
        background: '#fafafa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'

    },
    activeContainer: {
        border: `2px dashed ${theme.palette.primary.main}`,
    }
}))

function sizeValidator(file, maxSize) {
    if (maxSize !== undefined && file.size > maxSize * 1000000) {
        return {
            code: "file-too-large-custom",
            message: `File size is larger than ${maxSize} mb`
        }
    }

    return null;
}

const simulateUpload = (formData, chunkNumber, totalChunks) => {
    return new Promise((resolve, reject) => {
        const delay = 500 + Math.random() * 1000;
        setTimeout(() => {
            const success = Math.random() < 0.9;
            if (success) {
                console.log(`Uploaded chunk ${chunkNumber + 1}/${totalChunks}`);
                resolve({record: {id: uid(new Date())}});
            } else {
                reject(new Error('Simulated upload failure. Please try again.'));
            }
        }, delay);
    });
};
const getFileTypePrefix = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            // This will give you something like 'data:image/jpeg;base64,'
            const prefix = result.substring(0, result.indexOf(',') + 1);
            resolve(prefix);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

function formatBytes(bytes, decimals = 2) {
    if (!+bytes) return '0 Bytes'

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']

    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}


const DropzoneComponentChunks = (props) => {
    const {component, doActions, actionsContext, globalValues, screenName, containerArrayPath, setValue} = props;
    const styles = component.styles ? component.styles[0] : {};
    const dispatch = useDispatch();
    const classes = useStyles();
    const {properties} = component;
    const [droppedFiles, setDroppedFiles] = useState([]);
    const [onDropActions, setOnDropActions] = useState(null);
    const [fileTypes, setFileTypes] = useState([]);
    let arrayPath = setArrayPath(component.properties.path, containerArrayPath);
    const path = arrayPath ? arrayPath : component.properties.path;
    const {userLanguage} = useSelector(state => state.app);
    const multiLanguageLabel = multiLanguageValue(component.properties.label, userLanguage)
    let files = findByString(globalValues.param[screenName], path);
    const isDisabled = checkHidden({...globalValues, ...actionsContext}, properties.disabled, screenName);
    const allFieldsDisabled = useSelector(state => state.jsonScreen.allFieldsDisabled[screenName])

    useEffect(() => {
        const events = component.events || [];
        const actions = events[0] ? events[0].actions : undefined;
        actions && setOnDropActions(actions)
    }, [])

    useEffect(() => {
        async function getFileTypes() {
            try {
                const response = await request({
                    url: 'list/getData',
                    data: {id: 'fileTypeDefaultList'}
                });
                const fileTypes = response.content.map(item => {
                    return {
                        id: item.id,
                        name: multiLanguageValue(item.name, userLanguage),
                        association: item.association
                    }
                });
                return setFileTypes(fileTypes);
            } catch (e) {
                console.log('get file type error', e)
            }
        }

        getFileTypes()
    }, [userLanguage])

    useEffect(() => {
        setDroppedFiles(prev => {
            return !prev.length ? files ? Array.isArray(files) ? files.map(v => ({...v, progress: 100})) : [{
                ...files,
                progress: 100
            }] : [] : prev
        });
    }, [JSON.stringify(files)])

    const onDrop = useCallback(async (acceptedFiles) => {
        const readyFiles = [];
        const {isArray, fileType = 'system'} = component.properties;
        setDroppedFiles(acceptedFiles.map(v => {
            return {
                name: v.name,
                progress: 0,
                size: formatBytes(v.size)
            }
        }))

        for (let [index, item] of acceptedFiles.entries()) {
            const fileToUpload = item;
            const fileTypePrefix = await getFileTypePrefix(fileToUpload)
            const fileTypeExecuted = getParamsFromString(fileType, globalValues, screenName);
            const chunkSize = 1024 * 1024;
            const totalChunks = Math.ceil(fileToUpload.size / chunkSize);
            let currentChunk = 0;
            let offset = 0;
            let uploadResponse = {};
            while ((offset < fileToUpload.size) && !uploadResponse.canceled) {
                const chunk = fileToUpload.slice(offset, offset + chunkSize);
                const reader = new FileReader();
                const base64ToSend = await new Promise((resolve, reject) => {
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(chunk);
                });

                // Decode Base64 into a Uint8Array
                const parts = base64ToSend.split(',');

                const decodedBytes = Uint8Array.from(atob(parts[1]), (c) => c.charCodeAt(0));

                // Hash using SHA-256
                const hashBuffer = await crypto.subtle.digest("SHA-256", decodedBytes);

                // Convert to hex string
                const hashArray = Array.from(new Uint8Array(hashBuffer));

                const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");

                const formData = {
                    params: {
                        chunkIndex: currentChunk + 1,
                        totalChunks: totalChunks,
                    },
                    record: {
                        chunk: base64ToSend,
                        hash: hashHex,
                        filePrefix: (currentChunk === (totalChunks - 1)) ? fileTypePrefix.slice(0, fileTypePrefix.length - 1) : undefined,
                        name: fileToUpload.name,
                        id: uploadResponse.record?.id, // Send the fileId
                    }
                };
                try {
                    //uploadResponse = await simulateUpload(formData, currentChunk, totalChunks);
                    uploadResponse = await request({
                        url: "/files/upload",
                        data: formData
                    })
                    currentChunk++;
                    offset += chunkSize;
                    const newProgress = (currentChunk / totalChunks) * 100;
                    setDroppedFiles(prev => prev.map((v, i) => {
                        if (v.canceled) {
                            uploadResponse.canceled = v.canceled;
                            return
                        }
                        if (i === index) {
                            return {
                                ...v,
                                progress: newProgress
                            }
                        }
                        return v
                    }).filter(v => v))
                } catch (error) {
                    setDroppedFiles(prev => prev.map((v, i) => {
                        if (i === index) {
                            return {
                                ...v,
                                progress: 0,
                                errored: true
                            }
                        }
                        return v
                    }))

                    //exit from while loop
                    offset = fileToUpload.size

                }
            }

            if(!uploadResponse.canceled) {
                readyFiles.push({...uploadResponse.record, type: fileTypeExecuted})
            }

        }
        if (readyFiles && readyFiles.length) {
            setValue(isArray ? readyFiles : readyFiles[0], path)
        }


    }, [properties.uploadChunks, JSON.stringify(droppedFiles), fileTypes, onDropActions])

    const {getInputProps, getRootProps, isDragActive, fileRejections} = useDropzone({
        onDrop,
        noClick: component.properties.type === 'drag',
        noDrag: component.properties.type === 'select',
        disabled: Boolean(isDisabled || allFieldsDisabled),
        multiple: Boolean(component.properties.isArray),
        maxSize: properties.maxSize ? properties.maxSize * 1000000 : null,
        accept: component.properties.acceptTypes || component.properties.acceptedFileMask,
        validator: (f) => sizeValidator(f, properties.maxSize)
    });

    const setFilesInStorage = (files) => {
        let isArr = component.properties.isArray;
        if (!path) return;
        let action = {
            type: 'uploadFile',
            setToVar: path,
            multiple: component.properties.isArray,
            paramType: 'script',
            script: `{function setFiles(){ return ${JSON.stringify(isArr ? files : files[0])} }; setFiles();}`
        }

        doActions([action], actionsContext);
    }

    function onRemoveFile(index) {
        let currentFile = droppedFiles.find((v, i) => i === index);
        if (currentFile.progress !== 100) {
            setDroppedFiles(prev => prev.map((v, i) => {
                if (i === index) {
                    return {
                        ...v,
                        canceled: true
                    }
                }
                return v
            }))
            return
        }
        let newFiles = droppedFiles.filter((f, i) => i !== index);
        setDroppedFiles(newFiles)
        setFilesInStorage(newFiles)
    }

    function onChangeFileType(value, fPath) {
        dispatch(setFieldValue({
            value,
            path: `param.${screenName}.${path}.${fPath}`
        }))
    }

    //check is height defined in %
    let isPercentageHeight = styles.height && styles.height.substr(styles.height.length - 1) === '%';

    return (
        <div
            className={styles.className}
            style={{
                width: styles.width,
                height: isPercentageHeight ? styles.height : 'auto',
                display: 'flex',
                flexDirection: 'column'
            }}>
            <section {...getRootProps()}
                     className={clsx(classes.container,
                         {[classes.activeContainer]: isDragActive})}
                     style={{
                         height: isPercentageHeight ? '100%' : styles.height,
                         width: '100%'
                     }}
            >
                <div id={'dropzoneIcon'}
                     style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                    <IconCustom iconType={'symbols-outlined'}
                                style={{
                                    fontSize: 44,
                                    color: '#00B5F8'
                                }}
                    >
                        backup
                    </IconCustom>
                    <Typography color={'textSecondary'}>
                        {multiLanguageLabel}
                    </Typography>
                    <input
                        {...getInputProps({})}/>
                </div>
            </section>
            <div>
                {fileRejections.map(({_, errors}) => {
                    return (
                        <>
                            {errors.map(err => {
                                if (err.code === "file-too-large-custom") {
                                    return (
                                        <Typography variant={"subtitle2"} color={'secondary'}>{err.message}</Typography>
                                    )
                                }
                                if (err.code === 'file-invalid-type') {
                                    return (
                                        <Typography variant={"subtitle2"}
                                                    color={'secondary'}>{multiLanguageValue(properties.wrongTypeMessage || err.message, userLanguage)}</Typography>
                                    )
                                }
                            })}</>
                    )
                })}
            </div>
            {!properties.hideDroppedFiles && (<DroppedFilesList
                droppedFiles={droppedFiles}
                onRemoveFile={onRemoveFile}
                fileTypes={fileTypes}
                onChangeFileType={onChangeFileType}
                hideSelectType={component.properties.hideSelectType}
                isDisabled={Boolean(isDisabled || allFieldsDisabled)}
            />)}
        </div>

    )
}
export default JsonScreenHoc(DropzoneComponentChunks);
